import React, { useState } from 'lib/react';
import {
  EVGrid, EVTypography, EVButton, EVLink, EVAlert, EVCheckCircleOutlineIcon,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import {
  MANAGE_PAYMENT_OPTIONS_ROUTE,
  SUPPORT_ROUTE,
} from 'constants/RouteConstants';
import { Link } from 'lib/router';
import {
  LEGACY_PORTAL_LINKS, ACCOUNT_LEVEL, STATUS_SEVERITY_MAP, ERROR,
  SUCCESS,
} from 'constants/AppConstants';
import history from 'store/CreateHistory';
import MembershipLevel from 'commonComponents/membershipLevel/MembershipLevel.component';
import { useSelector } from 'lib/redux';
import { getUpgradeAccountLevel } from 'features/dashboard/Dashboard.utils';
import { getCurrencySymbol } from 'features/order/Order.utils';
import clsx from 'lib/clsx';
import Typography from 'commonComponents/typography';
import { isEntitledForHidePricingSelector } from 'utils/appSelectors.utils';
import AccountsSummaryStyles from './AccountsSummaryStyles';
import DashboardRetry from '../../DashboardRetry.component';

function AccountsSummary() {
  const { t } = useTranslation();
  const classes = AccountsSummaryStyles();
  const currentYear = new Date().getFullYear();
  const {
    currencyTypeId, accountSummary, accountSummaryFailure, isBillingAffiliate,
  } = useSelector(({ userData }) => ({
    // #genai: use optional chaining (Sonar S6582); userData.initParameters is either an object or null/undefined, so semantics are unchanged
    currencyTypeId: userData.initParameters?.currencyTypeId,
    accountSummary: userData.accountSummary,
    accountSummaryFailure: userData.error.accountSummary,
    isBillingAffiliate: userData.initParameters?.isBillingAffiliate,
  }));

  const [showLastDepositAlert, setShowLastDepositAlert] = useState(true);
  const handleLastDepositAlertClose = () => setShowLastDepositAlert(false);
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const {
    membershipLevel, reportsSentCount, reportsPendingCount, avgReportCost, ytdReportsReceived, ytdPrepayments, balance,
  } = accountSummary;

  const upgradeToLevel = getUpgradeAccountLevel(membershipLevel);
  const currency = getCurrencySymbol(currencyTypeId);
  const lastDepositStatusMessage = accountSummary?.lastDepositStatus?.split(' ').slice(0, -1).join(' ');
  const lastDepositStatus = accountSummary?.lastDepositStatus?.split(' ').pop();
  const lastDepositAlertSeverity = STATUS_SEVERITY_MAP[lastDepositStatus];

  return (
    <EVGrid className={classes.bottomSection}>
      <EVTypography className={classes.accountsSummaryText}>{t('dashboard__account_summary')}</EVTypography>
      {accountSummaryFailure ? (
        <DashboardRetry
          retryText={t('dashboard__account_summary_retry_msg')}
          action="LOAD_ACCOUNT_SUMMARY"
        />
      )
        : (
          <>
            {showLastDepositAlert && lastDepositStatus && (
            <EVAlert
              severity={lastDepositAlertSeverity}
              iconMapping={{ success: <EVCheckCircleOutlineIcon /> }}
              className={clsx(classes.lastDepositAlertContainer, lastDepositAlertSeverity)}
              onClose={lastDepositAlertSeverity === SUCCESS ? handleLastDepositAlertClose : null}
            >
              <Typography variant="body-400">
                {lastDepositStatusMessage}
                {' '}
                <b>{lastDepositStatus}</b>
                {/* #genai Keep the period adjacent to the status with no ambiguous JSX whitespace. */}
                .
              </Typography>
              {lastDepositAlertSeverity === ERROR && (
              <EVLink className={classes.alertErrorLink} onClick={() => history.push(SUPPORT_ROUTE)}>
                {t('common__contact_customer_service')}
              </EVLink>
              )}
            </EVAlert>
            )}
            <EVGrid className={classes.orderDetailsContainer}>
              <EVGrid className={classes.orderDetailsBox}>
                <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__total_order')}</EVTypography>
                <EVTypography className={classes.orderDetailsValueText}>{reportsSentCount}</EVTypography>
              </EVGrid>
              <EVGrid className={classes.orderDetailsBox}>
                <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__order_pending')}</EVTypography>
                <EVTypography className={classes.orderDetailsValueText}>{reportsPendingCount}</EVTypography>
              </EVGrid>
              <EVGrid className={classes.orderDetailsBox}>
                {!(isBillingAffiliate || isEntitledForHidePricing) && (
                  <>
                    <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__avg_report_cost')}</EVTypography>
                    <EVTypography className={classes.orderDetailsValueText}>
                      {`${currency} ${avgReportCost?.toFixed(2)}`}
                    </EVTypography>
                  </>
                )}
              </EVGrid>
            </EVGrid>
            <EVGrid className={classes.membershipLevelContainer}>
              <EVGrid className={classes.membershipLevelTopContainer}>
                <EVTypography className={classes.membershipLevelText}>{t('dashboard__membership_Level')}</EVTypography>
                <MembershipLevel level={membershipLevel} />
                <EVLink className={classes.membershipLevelLink} href={LEGACY_PORTAL_LINKS.pricing} target="_blank">
                  {t('dashboard__pricing_and_membership_level')}
                </EVLink>
              </EVGrid>
              {(membershipLevel !== ACCOUNT_LEVEL.PLATINUM && membershipLevel !== ACCOUNT_LEVEL.CUSTOM && currencyTypeId !== 4) && (
                <EVButton
                  id="membership-upgrade-btn"
                  component={Link}
                  to={MANAGE_PAYMENT_OPTIONS_ROUTE}
                  variant="outlined"
                  color="primary"
                  className={classes.upgradeButton}
                >
                  {t('dashboard__upgrade_to_level', { upgradeToLevel })}
                </EVButton>
              )}
              <EVGrid className={classes.yearsSummaryContainer}>
                <EVTypography className={classes.yearToDateText}>{t('dashboard__year_to_date', { year: currentYear })}</EVTypography>
                <EVGrid className={classes.yearsSummaryContentContainer}>
                  <EVGrid className={classes.yearsSummaryContentElement}>
                    <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__reports_recieved')}</EVTypography>
                    <EVTypography className={classes.orderDetailsValueText}>{ytdReportsReceived}</EVTypography>
                  </EVGrid>
                  {
                    membershipLevel !== ACCOUNT_LEVEL.GOLD && !isEntitledForHidePricing
                    && (
                    <EVGrid className={classes.yearsSummaryContentElement}>
                      <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__prepayments')}</EVTypography>
                      <EVTypography className={classes.orderDetailsValueText}>
                        {`${currency} ${ytdPrepayments?.toFixed(2)}`}
                      </EVTypography>
                    </EVGrid>
                    )
                  }
                  {
                      !isEntitledForHidePricing && (
                        <EVGrid className={clsx(classes.yearsSummaryContentElement, !(balance > 0) && 'negativeBalance')}>
                          <EVTypography className={classes.orderDetailsTitleText}>{t('dashboard__balance')}</EVTypography>
                          {(balance > 0) && (
                            <EVTypography className={classes.orderDetailsValueText}>
                              {`${currency} ${balance?.toFixed(2)}`}
                            </EVTypography>
                          )}
                        </EVGrid>
                      )
                    }
                </EVGrid>
              </EVGrid>
            </EVGrid>
          </>
        )}
    </EVGrid>
  );
}

export default AccountsSummary;
